import { Dictionary, groupBy, negate, orderBy, uniqBy } from 'lodash';
import moment, { Moment } from 'moment';
import { Module } from 'vuex';
import { EventInput } from '@fullcalendar/common';
import DossierClient from '@/dossier/client/DossierClient';
import VehicleStatus from '@/dossier/constants/VehicleStatus';
import WorkStatus from '@/dossier/constants/WorkStatus';
import { DossierId } from '@/dossier/models/AbstractDossier';
import WorkshopPlanningTeam, { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import AuthService from '@/general/services/AuthService';
import TimeService from '@/general/services/TimeService';
import TooltipService from '@/general/services/TooltipService';
import { RedirectDuringLoad } from '@/general/store/fetch';
import { RootState, WeekString, WorkshopPlanningState } from '@/general/store/states';
import Permission from '@/shared/constants/Permission';
import Outlet, { OutletId } from '@/shared/models/Outlet';
import { getById, mergeInto, removeBy } from '@/shared/utils';
import { ifPresent, isPresent, Nullish, present } from '@/shared/utils/typescript';
import WorkshopPlanningClient, { AbsenceData, ResourceContainer } from '../client/WorkshopPlanningClient';
import PlannerEventType from '../constants/PlannerEventType';
import PlannerView from '../constants/PlannerView';
import { PLANNER_ROOT_ROUTE, PLANNER_WEEK_FORMAT } from '../constants/general';
import Availability from '../models/Availability';
import Break from '../models/Break';
import EventContext from '../models/EventContext';
import PlannerCalendar from '../models/PlannerCalendar';
import PlannerCalendarEvent from '../models/PlannerCalendarEvent';
import PlannerDossier from '../models/PlannerDossier';
import PlannerEvent, { PlannerEventKey } from '../models/PlannerEvent';
import PlannerEventSchedule from '../models/PlannerEventSchedule';
import PlannerResource from '../models/PlannerResource';
import UnplannedEventUpdate from '../models/UnplannedEventUpdate';
import { AvailabilityLevel, Month } from '../models/common';
import { PlannerEventId, PlannerResourceId } from '../models/ids';
import PlannerParams, { PlannerRouteParams } from '../utils/PlannerParams';
import { wrapCalendarEvent } from '../utils/fullCalendarWrappers';
import { startPlannerPush, stopPlannerPush } from '../utils/plannerPushHelper';
import { ConfirmationPayload } from './dossierDetails';
import PlannerEventCreated from '../models/PlannerEventCreated';

interface CalendarPayload {
  calendar: PlannerCalendar;
  date: Moment;
}

interface PauseEventPayload {
  eventId: PlannerEventId;
  type: PlannerEventType;
}

interface UpdateEventStatusPayload {
  eventId: PlannerEventId;
  vehicleStatus: VehicleStatus;
  workStatus: WorkStatus;
}

interface UpdateEventEditablePayload {
  eventId: PlannerEventId;
  editable: boolean;
}

interface LoadCalendarPayload {
  outletId: OutletId;
  teamId: WorkshopTeamId;
  date: Moment;
}

interface LoadPlannedEventsPayload {
  outletId: OutletId;
  teamId: WorkshopTeamId;
  date: Moment;
}

interface LoadUnPlannedEventsPayload {
  outletId: OutletId;
  teamId: WorkshopTeamId;
}

interface ReschedulePayload {
  workshopPlanId: PlannerEventId;
  schedule: PlannerEventSchedule;
  assignedTo: PlannerResourceId;
}

interface CreateEventPayload {
  outletId: OutletId;
  event?: PlannerEvent;
  dossier?: PlannerDossier;
}

export interface MechanicPausedPayload {
  eventId: PlannerEventId;
  isPaused: boolean;
}

const CACHED_WEEKS_MAX = 10;

const module: Module<WorkshopPlanningState, RootState> = {
  namespaced: true,

  state: {
    params: null,
    date: null,
    cachedWeeks: [],
    calendarDate: null,
    resources: [],
    availabilities: [],
    plannedEvents: [],
    offPlanningEvents: [],
    currentPlannerEvent: null,
    lastCreatedPlannerEvent: null,
    currentEventCopy: null,
    draggedEvent: null,
    calendar: null,
    currentPlannerView: PlannerView.DAY,
    waitingCustomerBtnIsActive: false,
    userBtnIsActive: false,
    loaded: false,
    teamId: null,
  },

  mutations: {
    // Nav
    setParams(state, params: PlannerParams | null = null): void {
      state.params = params;
    },

    // Data
    setDate(state, date: Moment): void {
      state.date = date;
    },

    setTeamId(state, teamId: WorkshopTeamId): void {
      state.teamId = teamId;
    },

    setResources(state, pay: ResourceContainer): void {
      state.resources = pay.resources;
      state.availabilities = pay.availabilities;
    },

    addPlannedEvents(state, [events, week]: [Array<PlannerEvent>, WeekString]): void {
      state.cachedWeeks.push(week);
      while (state.cachedWeeks.length > CACHED_WEEKS_MAX) {
        const removedWeek = state.cachedWeeks.shift();
        state.plannedEvents = state.plannedEvents.filter(e => e.date?.format(PLANNER_WEEK_FORMAT) !== removedWeek);
      }
      state.plannedEvents.push(...events);
    },

    setUnplannedEvents(state, events: Array<PlannerEvent>): void {
      state.offPlanningEvents = events;
    },

    setCalendar(state, { calendar, date }: CalendarPayload): void {
      state.calendar = calendar;
      state.calendarDate = date;
    },

    // Current
    setCurrentPlannerEvent(state, event: PlannerEvent): void {
      state.currentPlannerEvent = event;
      state.currentEventCopy = event.copy();
    },

    setLastCreatedPlannerEvent(state, createdEvent: PlannerEventCreated): void {
      state.lastCreatedPlannerEvent = createdEvent;
    },

    setDraggedEvent(state, event: PlannerEvent | null = null): void {
      state.draggedEvent = event;
    },

    // Push updates
    rescheduleEvents(state, events: Array<PlannerEvent>): void {
      events.forEach(to => {
        state.plannedEvents.find(e => e.id === to.id)?.reschedule(to);
      });
    },

    pauseEvent(state, { eventId, type = PlannerEventType.PAUSED }: PauseEventPayload): void {
      const index = state.plannedEvents.findIndex((event) => event.id === eventId);
      if (index >= 0) {
        const plannedEvent = state.plannedEvents[index];
        plannedEvent.eventType = type;
        state.plannedEvents.splice(index, 1);
        state.offPlanningEvents.push(plannedEvent);
      }
    },

    stopEvent(state, eventId: PlannerEventId): void {
      const plannedEvent = state.plannedEvents.find((event) => event.id === eventId);
      if (plannedEvent) {
        plannedEvent.isStopped = true;
      }
    },

    updateEventStatus(state, payload: UpdateEventStatusPayload): void {
      const event = state.plannedEvents.find(e => e.id === payload.eventId)
        || state.offPlanningEvents.find(e => e.id === payload.eventId);
      if (event) {
        event.vehicleStatus = payload.vehicleStatus;
        event.workStatus = payload.workStatus;
      }
    },

    updateEventEditable(state, { eventId, editable }: UpdateEventEditablePayload): void {
      const plannedEvent = state.plannedEvents.find(e => e.id === eventId);
      if (plannedEvent) {
        plannedEvent.editable = editable;
      }
      if (state.currentPlannerEvent?.id === eventId) {
        state.currentPlannerEvent.editable = editable;
      }
    },

    updateEvent(state, event: PlannerEvent): void {
      const index = state.plannedEvents.findIndex(e => e.id === event.id);
      if (index >= 0) {
        state.plannedEvents[index] = event;
      }
    },

    addPlaceholderEvent(state, [event, outletId]: [PlannerEvent, OutletId]): void {
      if (event.eventType === PlannerEventType.PLANNED) {
        if (!state.plannedEvents.some(e => e.id === event.id)) state.plannedEvents.push(event);
      } else if (state.params?.outletId === outletId && (state.params.teamId === null || state.params.teamId === event.teamId)) {
        if (!state.offPlanningEvents.some(e => e.dossierId === event.dossierId)) state.offPlanningEvents.push(event);
      }
    },

    addPlannedEvent(state, event: PlannerEvent): void {
      if (state.date?.isSame(event?.start, 'week') && state.resources.some((resource) => resource.id === event.resourceId)) {
        mergeInto(event, state.plannedEvents);
        removeBy(state.offPlanningEvents, e => e.dossierId === event.dossierId);
      }
    },

    addUnplannedEvent(state, event: PlannerEvent): void {
      const currentTeamId = state.params?.teamId;
      if (!isPresent(currentTeamId) || currentTeamId === event.teamId) {
        mergeInto(event, state.offPlanningEvents, e => e.dossierId === event.dossierId);
      }
    },

    deletePlannedEvent(state, eventId: PlannerEventId): void {
      const index = state.plannedEvents.findIndex(e => e.id === eventId);
      if (index >= 0) {
        state.plannedEvents.splice(index, 1);
      }
    },

    updateIsMissingConfirmation(state, eventId: PlannerEventId): void {
      const plannedEvent = state.plannedEvents.find((event) => event.id === eventId);
      if (plannedEvent) {
        plannedEvent.isMissingConfirmation = false;
      }
      if (state.currentPlannerEvent?.id === eventId) {
        state.currentPlannerEvent.isMissingConfirmation = false;
      }
    },

    deleteAllEventsByDossierId(state, dossierId: DossierId): void {
      state.offPlanningEvents = state.offPlanningEvents.filter((event) => event.dossierId !== dossierId);
      state.plannedEvents = state.plannedEvents.filter((event) => event.dossierId !== dossierId);
    },

    updateUnplannedEvent(state, update: UnplannedEventUpdate): void {
      const unplannedEvent = state.offPlanningEvents.find((event) => event.dossierId === update.dossierId);
      if (unplannedEvent) {
        unplannedEvent.teamId = update.teamId;
        unplannedEvent.vehicleStatus = update.vehicleStatus;
        unplannedEvent.checkedInAt = update.checkedInAt;
      }
    },

    updateIsMechanicPaused(state, payload: MechanicPausedPayload): void {
      const event = state.plannedEvents.find(e => e.id === payload.eventId);
      if (event) {
        event.isMechanicPaused = payload.isPaused;
      }
    },

    setLoaded(state, loaded: boolean): void {
      state.loaded = loaded;
    },

    // Other updates
    updateEventCopyDuration(state, duration: string): void {
      if (state.currentEventCopy) {
        state.currentEventCopy.duration = duration;
      }
    },

    // Calendar view toggle
    updateCurrentPlannerView(state, view: PlannerView): void {
      state.currentPlannerView = view;
    },

    // Week view btn toggles
    setWaitingCustomerBtn(state, val: boolean): void {
      state.waitingCustomerBtnIsActive = val;
    },

    setUserBtn(state, val: boolean): void {
      state.userBtnIsActive = val;
    },

    // Cleanup
    clearCurrent(state): void {
      state.currentPlannerEvent = null;
    },

    clearCalendar(state): void {
      state.calendar = null;
    },

    clearPlannedEvents(state): void {
      state.cachedWeeks = [];
      state.plannedEvents = [];
    },

    closeDay(state, date: Moment): void {
      state.plannedEvents.filter(e => e.canBeClosedOn(date)).forEach(e => e.isStopped = true);
    },

    close(state): void {
      stopPlannerPush();
      state.params = state.params?.primary() ?? null;
      state.date = null;
      state.currentPlannerEvent = null;
      state.loaded = false;
      state.cachedWeeks = [];
      state.plannedEvents = [];
    },
  },

  getters: {
    activeOutlet(state): Outlet {
      const outletId = present(state.params).outletId;
      return AuthService.getCurrentUser().getOutlet(outletId);
    },

    activeTeam(state): WorkshopPlanningTeam | null {
      const teamId = present(state.params).teamId;
      if (teamId === null) return null;
      return AuthService.getCurrentUser().getTeam(teamId);
    },

    waitingOnPromiseEvents(state): Array<PlannerEvent> {
      return state.plannedEvents.filter(event => event.waitsOnPremise);
    },

    pickupAtEvents(state): Array<PlannerEvent> {
      return state.plannedEvents?.filter(event => event.pickUpAt && event.isCheckedOut) ?? [];
    },

    myDossierEvents(state): Array<PlannerEvent> {
      const eventsMyDossiers = state.plannedEvents.filter(event => event?.dossierOwner === AuthService.getCurrentUser()?.id);
      if (state.currentPlannerView === PlannerView.DAY) {
        return eventsMyDossiers.filter((event) => state.date?.isSame(event.date, 'day'));
      }
      return eventsMyDossiers;
    },

    breakEvents(state): Array<Break> {
      return state.resources?.flatMap(r => r.breaks);
    },

    getResourceById(state): (resourceId: Nullish<PlannerResourceId>) => PlannerResource | undefined {
      return resourceId => ifPresent(resourceId, id => getById(state.resources, id));
    },

    dossiersByCategory(state, getters, rootState): Dictionary<Array<PlannerEvent>> {
      const settings = rootState.workshopPlanningUserSettings;
      const filtered = state.offPlanningEvents.filter(e => e.eventType === PlannerEventType.NONPLANNED && !e.isFiltered(settings));
      const ordered = orderBy(filtered, 'start');
      const now = TimeService.now.value;
      return groupBy(ordered, e => e.getDossierCategory(now) ?? '');
    },

    offPlanningPausedEvents(state): Array<PlannerEvent> {
      return state.offPlanningEvents.filter(event => event.eventType === PlannerEventType.PAUSED);
    },

    getEventByKey(state): (key: PlannerEventKey) => PlannerEvent | undefined {
      return key => [
        state.currentPlannerEvent,
        state.currentEventCopy,
        ...state.plannedEvents,
        ...state.offPlanningEvents,
      ].find(event => event?.key === key) ?? undefined;
    },

    getCalendarEvents: (state) => (availabilityLevel: AvailabilityLevel | null, today: boolean): Array<EventInput> => {
      if (!state.calendar) return [];
      const isToday = (event: PlannerCalendarEvent): boolean => event.date.isSame(state.date, 'day');
      return state.calendar.events
        .filter((event) => (event.availabilityLevel === availabilityLevel))
        .filter(today ? isToday : negate(isToday))
        .map(wrapCalendarEvent);
    },

    currentAvailability(state): Availability | undefined {
      return state.availabilities?.find(availability => availability.dayOfWeek === state.date?.day());
    },

    closeDayAvailable(state): boolean {
      if (!AuthService.getCurrentUser().hasPermission(Permission.WORKSHOP_PLANNING_CLOSE_DAY)) return false;
      const now = TimeService.now.value;
      if (state.calendar?.closeDayFrom) {
        return now.isAfter(state.calendar.closeDayFrom);
      }
      return (state.date?.isSame(now, 'day') ?? false) && now.hour() >= 12;
    },

    visibleMechanics(state, getters, rootState): Array<PlannerResource> {
      if (!rootState.workshopPlanningUserSettings.hideUnavailableMechanics) return state.resources;
      if (state.currentPlannerView === PlannerView.DAY) {
        return getters._getAvailableMechanicsByDate(state.date);
      } else {
        const firstWeekDay = present(state.date).clone().startOf('week');
        const businessDays = state.availabilities.filter(a => !a.isClosed).map(a => firstWeekDay.clone().add(a.dayOfWeek, 'days'));
        const allMechanics = businessDays.flatMap(day => getters._getAvailableMechanicsByDate(day));
        return uniqBy(allMechanics, 'id');
      }
    },

    _getAvailableMechanicsByDate: (state) => (date: Moment): Array<PlannerResource> => {
      return state.resources.filter(resource => {
        const plannedEvents = state.plannedEvents.filter(event => event.resourceId === resource.id && event.date?.isSame(date, 'day'));
        return resource.isAvailable(date, plannedEvents);
      });
    },
  },

  actions: {
    async open({ commit, dispatch, state }, params: PlannerRouteParams): Promise<void> {
      const user = AuthService.getCurrentUser();
      const outlets = user.outlets;
      const current = state.params ?? PlannerParams.basic(
        moment(),
        outlets[0].id,
      );

      const parsed = PlannerParams.parse(params, current, outlets, user.isValidTeam.bind(user));

      if (parsed.corrected) {
        throw new RedirectDuringLoad((to, from, next) => {
          next(parsed.asLocation(to.name ?? PLANNER_ROOT_ROUTE));
        });
      }

      const promises = [
        dispatch('_loadCalendar', parsed),
        dispatch('_loadPlannedEvents', parsed),
        dispatch('_loadUnplannedEvents', parsed),
      ];

      if (!state.loaded || parsed.outletId !== state.params?.outletId) {
        startPlannerPush(parsed.outletId, commit, dispatch);
      }

      await Promise.all(promises);

      commit('setParams', parsed);
      commit('setDate', parsed.date);
      commit('updateCurrentPlannerView', parsed.view);
      commit('setWaitingCustomerBtn', parsed.showWaiting);
      commit('setUserBtn', parsed.showUser);
      commit('setLoaded', true);
      commit('setTeamId', parsed.teamId);
    },

    async loadCalendar({ state, dispatch }, date: Moment): Promise<void> {
      await dispatch('_loadCalendar', { ...state.params, teamId: state.teamId, date });
    },

    async _loadCalendar({ state, commit }, { outletId, teamId, date }: LoadCalendarPayload): Promise<void> {
      const outletChanged = state.params?.outletId !== outletId;
      const monthChanged = !state.calendarDate?.isSame(date, 'month');
      if (!state.loaded || outletChanged || monthChanged) {
        const calendar = await WorkshopPlanningClient.loadCalendar(outletId, teamId, date.month() + 1 as Month, date.year());
        commit('setCalendar', { date, calendar });
      }
    },

    async _loadPlannedEvents({ state, commit, dispatch }, { outletId, teamId, date }: LoadPlannedEventsPayload): Promise<void> {
      const outletChanged = state.params?.outletId !== outletId;
      const teamChanged = state.params?.teamId !== teamId;
      const weekChanged = !state.params?.date.isSame(date, 'week');
      if (outletChanged || teamChanged) {
        commit('clearPlannedEvents');
      }
      if (!state.loaded || outletChanged || teamChanged || weekChanged) {
        await dispatch('_loadPlannerResources', { outletId, teamId, date });
        const resourceIds = state.resources.map(r => present(r.id));
        const week = date.format(PLANNER_WEEK_FORMAT);
        if (!state.cachedWeeks.includes(week)) {
          const calendar = await WorkshopPlanningClient.loadCalendar(outletId, teamId, date.month() + 1 as Month, date.year());
          const plannedEvents = await WorkshopPlanningClient.loadEvents(outletId, teamId, date, resourceIds);
          commit('setCalendar', { date, calendar });
          commit('addPlannedEvents', [plannedEvents, week]);
        }
      }
    },

    async _loadUnplannedEvents({ state, commit }, { outletId, teamId }: LoadUnPlannedEventsPayload): Promise<void> {
      const outletChanged = state.params?.outletId !== outletId;
      const teamChanged = state.params?.teamId !== teamId;
      if (!state.loaded || outletChanged || teamChanged) {
        const offPlanning = await WorkshopPlanningClient.loadOffPlanning(outletId, teamId);
        commit('setUnplannedEvents', offPlanning);
      }
    },

    async _loadPlannerResources({ commit }, { outletId, teamId, date }) {
      const resourcesContainer = await WorkshopPlanningClient.loadResources(outletId, teamId, date);
      commit('setResources', resourcesContainer);
    },

    async refreshPlannerResources({ state, dispatch }) {
      if (!state.params) return;

      dispatch('_loadPlannerResources', { outletId: state.params.outletId, teamId: state.params.teamId, date: state.params.date });
    },

    async openEvent({ state, commit, dispatch, rootState }): Promise<void> {
      const eventId = present(state.params?.eventId);
      if (state.currentPlannerEvent?.id === eventId) return;
      const event = state.plannedEvents.find(e => e.id === eventId);
      if (!event) {
        // if the event couldn't be found, it's probably not in the current week
        // lacking an API to load a specific event, we just redirect to the planner root for now
        throw new RedirectDuringLoad((to, from, next) => {
          next(present(state.params).asRootLocation());
        });
      }
      if (isPresent(event.dossierId)) {
        await dispatch('dossierDetails/load', event.dossierId, { root: true });
      } else {
        commit('dossierDetails/clear', undefined, { root: true });
      }
      commit('setCurrentPlannerEvent', event);
      commit('updateEventCopyDuration', rootState.dossierDetails.current?.duration);
    },

    async openDossier({ state, rootState, commit, dispatch }): Promise<void> {
      const dossierId = present(state.params?.dossierId);
      await dispatch('dossierDetails/load', dossierId, { root: true });
      const dossierDetails = present(rootState.dossierDetails.current);
      const event = state.offPlanningEvents.find(e => e.dossierId === dossierId) ?? PlannerEvent.dummy(dossierDetails);
      commit('setCurrentPlannerEvent', event);
      commit('updateEventCopyDuration', dossierDetails.duration);
    },

    async openDossierList({ commit }): Promise<void> {
      commit('clearCurrent');
    },

    async reschedule({ }, pay: ReschedulePayload): Promise<void> {
      await WorkshopPlanningClient.reschedule(pay.workshopPlanId, pay.schedule.start, pay.schedule.end, pay.assignedTo);
    },

    createEvent({ state, dispatch }, event: PlannerEvent): Promise<PlannerEvent> {
      const outletId = present(state.params).outletId;
      const pay: CreateEventPayload = { outletId, event };
      return dispatch('_createEvent', pay);
    },

    createDossier({ dispatch }, context: EventContext): Promise<PlannerEvent> {
      const dossier = present(context.dossier);
      const pay: CreateEventPayload = {
        outletId: present(dossier.outlet?.id),
        dossier,
        event: context.event ?? undefined,
      };
      return dispatch('_createEvent', pay);
    },

    async _createEvent({ commit }, pay: CreateEventPayload): Promise<PlannerEvent> {
      let dossierId = pay.event?.dossierId;
      let eventCreated: PlannerEventCreated | undefined;

      if (pay.dossier) {
        dossierId = await WorkshopPlanningClient.createPlanningDossier(pay.dossier);
        if (pay.dossier.dossierTags.length > 0) {
          await DossierClient.updateTags(dossierId, pay.dossier.dossierTags);
        }
      }

      if (pay.event) {
        pay.event.dossierId = dossierId;
        eventCreated = await WorkshopPlanningClient.createEvent(pay.outletId, pay.event);
      }

      const placeholder = PlannerEvent.createPlaceholder(pay.event, eventCreated?.id, pay.dossier, dossierId);
      commit('addPlaceholderEvent', [placeholder, pay.outletId]);
      
      if (eventCreated) {
        commit('setLastCreatedPlannerEvent', eventCreated);
      }

      return placeholder;
    },

    async createInternalEvent({ state }, event: PlannerEvent): Promise<void> {
      await WorkshopPlanningClient.createInternalEvent(present(state.params?.outletId), event);
    },

    async sendConfirmation({ state, commit }, pay: ConfirmationPayload): Promise<void> {
      const eventId = present(state.currentPlannerEvent?.id);
      await WorkshopPlanningClient.sendConfirmation(eventId, pay.appointment, pay.preferredPickUp, pay.targets);
      commit('dossierDetails/updateConfirmationTimings', pay, { root: true });
      if (pay.targets.length > 0) {
        commit('updateIsMissingConfirmation', eventId);
      }
    },

    async deleteDossierEvent({ commit }, dossierId: DossierId): Promise<void> {
      await DossierClient.remove(dossierId);
      commit('deleteAllEventsByDossierId', dossierId);
    },

    async closeDay({ state, rootState, getters, commit }): Promise<void> {
      if (!getters.closeDayAvailable) return;
      await WorkshopPlanningClient.closeDay(present(state.params?.outletId));
      commit('closeDay', rootState.time.now);
    },

    async eventDragStart({ commit }, event: PlannerEvent): Promise<void> {
      TooltipService.disable();
      commit('setDraggedEvent', event);
    },

    async eventDragStop({ commit }): Promise<void> {
      TooltipService.enable();
      commit('setDraggedEvent');
    },

    async createAbsence({ state }, data: AbsenceData): Promise<void> {
      const outletId = present(state.params?.outletId);
      await WorkshopPlanningClient.createAbsence(outletId, data);
    },
  },
};

export default module;
